import {BOARD_ROLE_ID, IUser, MEMBER_ROLE_ID, QUALITY_ROLE_ID} from "./IUser";
import {routes} from "../utils/routes";

/**
 * Способности роли на фронте (ROLES.md § 4, S20.1). Компоненты спрашивают
 * способность, а не «это роль N»: одна проверка — одна способность.
 */
export interface Permissions {
    /** Боковое меню, навбар, профиль в шапке доски; пункты — по `routes` (S20.2: у всех ролей). */
    menu: boolean;
    /** Маршруты кроме доски: все или список путей; не в списке — на доску. */
    routes: "all" | string[];
    /** «+ Машина». */
    createCar: boolean;
    /** Журнал. */
    journal: boolean;
    /** Общий чат (в т. ч. счётчик и уведомления). */
    generalChat: boolean;
    /** Перетаскивание между колонками / «Перенести» на телефоне. */
    dragCars: boolean;
    /** Взять, вехи, алерт, доработка, «⋯», стенды, «Уехала». */
    carActions: boolean;
    /** Писать в тред машины, вложения, реакции. */
    threadWrite: boolean;
    /** DID / DTC: загрузить, удалить столбец. */
    uploadSnapshot: boolean;
    /** ФЧ: добавить, править, устранить / вернуть, удалить своё замечание (S21.2). */
    fchWrite: boolean;
    /** Колокольчик, всплывающие, звук, системные уведомления. */
    notifications: boolean;
    /** «Стата»: вкладка «Смены» (смены, праздники). */
    statsEdit: boolean;
    /** Непрочитанное: бейджи, `GET /chat/unread`, `POST /chat/read`. */
    unread: boolean;
}

const MEMBER: Permissions = {
    menu: true,
    routes: "all",
    createCar: true,
    journal: true,
    generalChat: true,
    dragCars: true,
    carActions: true,
    threadWrite: true,
    uploadSnapshot: true,
    fchWrite: true,
    notifications: true,
    statsEdit: true,
    unread: true,
};

export const PERMISSIONS_BY_ROLE: Record<number, Permissions> = {
    [MEMBER_ROLE_ID]: MEMBER,
    [BOARD_ROLE_ID]: {
        menu: true,
        routes: [],
        createCar: false,
        journal: false,
        generalChat: false,
        dragCars: true,
        carActions: false,
        threadWrite: true,
        uploadSnapshot: false,
        fchWrite: false,
        notifications: true,
        statsEdit: false,
        unread: true,
    },
    [QUALITY_ROLE_ID]: {
        menu: true,
        routes: [routes.stats],
        createCar: false,
        journal: false,
        generalChat: false,
        dragCars: false,
        carActions: false,
        threadWrite: false,
        uploadSnapshot: false,
        fchWrite: false,
        notifications: false,
        statsEdit: false,
        unread: false,
    },
};

/** Способности пользователя; нет пользователя или неизвестная роль — как у сотрудника. */
export const permissionsOf = (user: Pick<IUser, "role_id"> | null | undefined): Permissions =>
    PERMISSIONS_BY_ROLE[user?.role_id ?? MEMBER_ROLE_ID] ?? MEMBER;

/** Доступен ли маршрут: доска — всем, остальное — по `routes`. Одна проверка для роутера и меню. */
export const canRoute = (can: Pick<Permissions, "routes">, path: string): boolean =>
    can.routes === "all" || path === routes.machines || can.routes.includes(path);
